import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useRecoilValue } from 'recoil'
import { useTranslation } from 'react-i18next'
import { userAtom } from '@/atoms/_globalAtoms'
import { Icon } from '@/components/priority/Icon/Icon'
import { useSetToast } from '@/hooks/utility/useSetToast'
import { useAiImageEditsEnabled } from '@/hooks/caseImages/useAiImageEditsEnabled'
import {
  useBackgroundRemoval,
  type BackgroundRemovalResult,
} from '@/hooks/editor/useBackgroundRemoval'
import { BackgroundRemovalPanel } from './BackgroundRemovalPanel'
import * as S from './BackgroundRemoval.styled'

/** Selector for the util tab list (Crop, Filter, ...) inside a Pintura root. */
const RAIL_SELECTOR = '.PinturaNavMain .PinturaTabList'

/** The rail tab's test id; tests import it from here. */
export const TOOL_TAB_TEST_ID = 'bg-removal-tool-tab'

type BackgroundRemovalToolProps = {
  /** Pintura's `.PinturaRoot` element once the editor has loaded its image. */
  editorRoot: HTMLElement | null
  /** The photo Pintura is editing (no cache-bust query). */
  imageUrl: string
  /** The CaseImage row behind the item; null hides the tool. */
  caseImageId: string | null
  /** The accepted cutout the item already shows, or null. */
  appliedCutout: BackgroundRemovalResult | null
  /** Writes an accepted cutout onto the editor item; true once it is there. */
  onApply: (result: BackgroundRemovalResult) => Promise<boolean>
  /** Pintura shows the original photo again in place of the item's cutout. */
  onUseOriginal: () => void
  /** Close the Adjust Image modal after the cutout is applied. */
  onDone: () => void
}

/**
 * Adds the "Remove Background" entry to Pintura's util rail and shows the
 * background-removal screens over the stage while it is active.
 *
 * Pintura renders its own (Svelte) DOM, so the rail entry and the panel are
 * portaled into it rather than composed in React: the tab goes into the
 * existing tab list so it inherits Pintura's tab styling, and the panel goes
 * into the editor root so it covers exactly the stage and toolbar.
 *
 * While AI image edits are off (`useAiImageEditsEnabled`) the tool adds no
 * tab, so Adjust Image shows only Pintura's own utils.
 */
export const BackgroundRemovalTool = ({
  editorRoot,
  imageUrl,
  caseImageId,
  appliedCutout,
  onApply,
  onUseOriginal,
  onDone,
}: BackgroundRemovalToolProps) => {
  const { t } = useTranslation()
  const user = useRecoilValue(userAtom)
  const setToast = useSetToast()
  const aiImageEditsEnabled = useAiImageEditsEnabled()
  const tabRef = useRef<HTMLLIElement>(null)

  const removal = useBackgroundRemoval({
    caseImageId,
    imageUrl,
    userId: user?.id,
    appliedCutout,
    onApply,
    onAcceptError: () =>
      setToast({ text: t('editor.backgroundRemoval.acceptFailed') }),
  })
  const { step, close } = removal
  const isOpen = step !== 'idle'
  const closeRef = useRef(close)
  closeRef.current = close

  // Pintura draws its util rail after the load event that hands us the
  // editor root, and mounts a new tab list whenever it hides and shows its
  // utils again (the nav sits in an `{#if}` block), so the root is watched
  // for as long as the tool is mounted and the tab follows whichever list
  // is in the DOM now. Read once, the tab never appears when the root
  // arrives before the list exists; watched only until the first list, it
  // is lost with that list. Setting the same list again is a no-op, so this
  // tool's own portal writes into the list do not re-render it.
  const [rail, setRail] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (!editorRoot) {
      setRail(null)
      return
    }
    const findRail = () =>
      setRail(editorRoot.querySelector<HTMLElement>(RAIL_SELECTOR))
    findRail()
    const observer = new MutationObserver(findRail)
    observer.observe(editorRoot, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [editorRoot])

  // Pintura's own tabs are outside React, so switching to Crop/Filter while
  // this tool is open is observed with a native listener on the rail.
  useEffect(() => {
    if (!rail || !isOpen) return
    const handleRailClick = (event: MouseEvent) => {
      if (tabRef.current?.contains(event.target as Node)) return
      closeRef.current()
    }
    rail.addEventListener('click', handleRailClick)
    return () => rail.removeEventListener('click', handleRailClick)
  }, [rail, isOpen])

  if (!aiImageEditsEnabled || !editorRoot || !rail || !caseImageId) return null

  const handleTabClick = () => {
    if (isOpen) return
    void removal.open()
  }

  return (
    <>
      {createPortal(
        <S.RailTab
          ref={tabRef}
          aria-selected={isOpen}
          data-bg-removal-tab="true"
        >
          <button
            type="button"
            role="tab"
            aria-selected={isOpen}
            onClick={handleTabClick}
            data-testid={TOOL_TAB_TEST_ID}
          >
            <Icon icon="sparkles" fill="pinturaForeground" />
            <span>{t('editor.backgroundRemoval.toolLabel')}</span>
          </button>
        </S.RailTab>,
        rail,
      )}
      {isOpen &&
        createPortal(
          <BackgroundRemovalPanel
            originalUrl={imageUrl}
            removal={removal}
            onCancel={close}
            onUseOriginal={() => {
              close()
              onUseOriginal()
            }}
            onDone={() => {
              close()
              onDone()
            }}
          />,
          editorRoot,
        )}
    </>
  )
}
